import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"
import { HugeiconsIcon, Icons } from "@/components/icons"

import { AVACUS_SNS_WEB_URL } from "@/configs/common"
import { Button } from "@/components/ui/button"
import { CopyButton } from "@/components/ui/copy-button"
import type { SnsUser } from "@/types/user"
import { UserAvatar } from "../user-avatar"
import { cn } from "@/lib/utils"
import { shortenAddressWithCenterDots } from "@/lib/utils"
import { useAuth } from "@/contexts/auth-context"
import { useIntl } from "react-intl"

export interface UserDisplayProps {
  /** When true, trigger shows only the avatar (no name, no arrow). */
  avatarOnly?: boolean
  /** Applied to the trigger button (for pill/mobile header styling). */
  triggerClassName?: string
}

export function UserDisplay({
  avatarOnly,
  triggerClassName,
}: UserDisplayProps = {}) {
  const intl = useIntl()
  const { address, displayName, user, disconnectWallet } = useAuth()
  const snsUser = user as SnsUser | undefined

  if (!address) return null

  const handleDisconnect = async () => {
    await disconnectWallet()
  }

  const displayText =
    displayName ??
    snsUser?.display_name ??
    shortenAddressWithCenterDots(address, 4, 6)
  const shortAddress = shortenAddressWithCenterDots(address, 4, 6)
  const profileUrl =
    (snsUser?.wallet_address || address) &&
    `${AVACUS_SNS_WEB_URL}/profile/${snsUser?.wallet_address ?? address}`

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost"
          className={cn(
            !avatarOnly &&
              "border-primary-50 bg-secondary-background flex h-10 items-center gap-2 rounded-xl border px-[10px] py-2",
            avatarOnly && "flex size-9 shrink-0 items-center rounded-full p-0",
            triggerClassName
          )}
        >
          <UserAvatar
            address={address}
            avatarUrl={snsUser?.avatar?.url}
            nftAvatar={
              snsUser?.nft_avatar as
                | {
                    avatar_url?: string
                    avatar_crop_url?: string
                    animation_url?: string
                  }
                | undefined
            }
            size={avatarOnly ? 36 : 20}
            className={avatarOnly ? "size-7 shrink-0" : "size-5 shrink-0"}
          />
          {!avatarOnly && (
            <>
              <span className="app-text-title-small text-primary-text hidden max-w-[120px] truncate select-none sm:inline-block">
                {displayText}
              </span>
              <HugeiconsIcon
                icon={Icons.ArrowDown01}
                size={16}
                className="text-muted-foreground hidden shrink-0 sm:block"
              />
            </>
          )}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        align="end"
        sideOffset={8}
        className="border-border bg-background shadow-1-light w-64 overflow-hidden rounded-2xl border p-2"
      >
        <div className="flex items-center gap-2 p-2">
          <UserAvatar
            address={address}
            avatarUrl={snsUser?.avatar?.url}
            nftAvatar={
              snsUser?.nft_avatar as
                | {
                    avatar_url?: string
                    avatar_crop_url?: string
                    animation_url?: string
                  }
                | undefined
            }
            size={40}
            className="size-10 shrink-0 rounded-full"
          />
          <div className="w-fit min-w-0">
            <p className="text-primary-text app-text-title-medium truncate">
              {displayText}
            </p>
            <div className="text-secondary-text app-text-body-small mt-1 flex items-center gap-1">
              <span className="truncate">{shortAddress}</span>
              <CopyButton
                text={address}
                size="icon"
                variant="ghost"
                className="text-primary hover:text-primary-75 h-6 w-6 shrink-0 rounded p-0"
                iconSize={14}
              />
            </div>
          </div>
        </div>

        <DropdownMenuSeparator className="separator-dashed-10 my-2 bg-transparent" />

        {profileUrl && (
          <DropdownMenuItem asChild>
            <a
              href={profileUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="focus:bg-accent-25 flex h-10 cursor-pointer items-center gap-2 rounded-xl px-2.5 py-2 outline-none"
            >
              <HugeiconsIcon
                icon={Icons.SquareArrowUpRight}
                size={20}
                className="text-primary-text shrink-0"
              />
              <span className="text-primary-text app-text-title-small">
                {intl.formatMessage({ id: "wallet.viewProfile" })}
              </span>
            </a>
          </DropdownMenuItem>
        )}

        <DropdownMenuItem asChild>
          <a
            href="https://contact.sowaka.io/"
            target="_blank"
            rel="noopener noreferrer"
            className="focus:bg-accent-25 flex h-10 cursor-pointer items-center gap-2 rounded-xl px-2.5 py-2 outline-none"
          >
            <HugeiconsIcon
              icon={Icons.Mail01}
              size={20}
              className="text-primary-text shrink-0"
            />
            <span className="text-primary-text app-text-title-small">
              {intl.formatMessage({ id: "wallet.contactSupport" })}
            </span>
          </a>
        </DropdownMenuItem>

        <DropdownMenuItem asChild>
          <a
            href="https://avacus.cc/policy"
            target="_blank"
            rel="noopener noreferrer"
            className="focus:bg-accent-25 flex h-10 cursor-pointer items-center gap-2 rounded-xl px-2.5 py-2 outline-none"
          >
            <HugeiconsIcon
              icon={Icons.Shield01}
              size={20}
              className="text-primary-text shrink-0"
            />
            <span className="text-primary-text app-text-title-small">
              {intl.formatMessage({ id: "wallet.privacyPolicy" })}
            </span>
          </a>
        </DropdownMenuItem>

        <DropdownMenuItem asChild>
          <a
            href="https://avacus.cc/terms"
            target="_blank"
            rel="noopener noreferrer"
            className="focus:bg-accent-25 flex h-10 cursor-pointer items-center gap-2 rounded-xl px-2.5 py-2 outline-none"
          >
            <HugeiconsIcon
              icon={Icons.GoogleDoc}
              size={20}
              className="text-primary-text shrink-0"
            />
            <span className="text-primary-text app-text-title-small">
              {intl.formatMessage({ id: "wallet.termsOfService" })}
            </span>
          </a>
        </DropdownMenuItem>

        <DropdownMenuSeparator className="separator-dashed-10 my-2 bg-transparent" />

        <DropdownMenuItem
          onClick={handleDisconnect}
          className="text-error focus:bg-error-5 focus:text-error hover:bg-error-5 flex h-10 cursor-pointer items-center gap-2 rounded-xl px-2.5 py-2 outline-none"
        >
          <HugeiconsIcon
            icon={Icons.Connect}
            size={20}
            className="text-error shrink-0"
          />
          <span className="app-text-title-small text-error">
            {intl.formatMessage({ id: "wallet.disconnect" })}
          </span>
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  )
}
